import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { FileText, AlertTriangle } from 'lucide-react'
import { type KBSettings } from '@/hooks/useSettings'

interface ParserSettingsCardProps {
  formData: KBSettings
  handleChange: (field: keyof KBSettings, value: any) => void
}

export function ParserSettingsCard({ formData, handleChange }: ParserSettingsCardProps) {
  // Methods that don't support parser_config (per RAGflow docs)
  const noParserConfigMethods = ['table', 'picture', 'one', 'email']
  const isParserConfigDisabled = noParserConfigMethods.includes(formData.chunk_method)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          Parser Settings
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <Alert className="border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950/30">
            <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
            <AlertDescription className="text-yellow-900 dark:text-yellow-100 text-sm">
              Changes to parser settings require reparsing all documents.
              This may take time depending on your document count.
            </AlertDescription>
          </Alert>

          {/* Chunking Method */}
          <div className="space-y-2">
            <Label htmlFor="chunk_method">Chunking Method</Label>
            <Select
              value={formData.chunk_method}
              onValueChange={(value) => handleChange('chunk_method', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="naive">Naive - Simple text splitting</SelectItem>
                <SelectItem value="manual">Manual - Custom rules</SelectItem>
                <SelectItem value="qa">Q&A - Question-answer pairs</SelectItem>
                <SelectItem value="one">One - Entire document as single chunk</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              How documents are split into chunks
            </p>
          </div>

          {/* Parser config notice for methods that don't support it */}
          {isParserConfigDisabled && (
            <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30">
              <AlertDescription className="text-blue-900 dark:text-blue-100 text-sm">
                The "{formData.chunk_method}" chunking method does not support parser configuration.
                {formData.chunk_method === 'one' && ' The entire document will be treated as a single chunk.'}
              </AlertDescription>
            </Alert>
          )}

          {/* Chunk Size */}
          <div className={`space-y-2 ${isParserConfigDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <Label htmlFor="chunk_token_count">Chunk Size (tokens)</Label>
            <Input
              id="chunk_token_count"
              type="number"
              min={32}
              max={2048}
              value={formData.chunk_token_count}
              onChange={(e) => handleChange('chunk_token_count', parseInt(e.target.value) || 128)}
              disabled={isParserConfigDisabled}
            />
            <p className="text-xs text-muted-foreground">
              Number of tokens per chunk (32-2048)
            </p>
          </div>

          {/* Language */}
          <div className={`space-y-2 ${isParserConfigDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <Label htmlFor="language">Language</Label>
            <Select
              value={formData.language}
              onValueChange={(value) => handleChange('language', value)}
              disabled={isParserConfigDisabled}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="English">English</SelectItem>
                <SelectItem value="Chinese">Chinese</SelectItem>
                <SelectItem value="Spanish">Spanish</SelectItem>
                <SelectItem value="French">French</SelectItem>
                <SelectItem value="German">German</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Primary language of your documents
            </p>
          </div>

          {/* Delimiter */}
          <div className={`space-y-2 ${isParserConfigDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <Label htmlFor="delimiter">Text Delimiters</Label>
            <Input
              id="delimiter"
              type="text"
              value={formData.delimiter}
              onChange={(e) => handleChange('delimiter', e.target.value)}
              placeholder="\n"
              disabled={isParserConfigDisabled}
            />
            <p className="text-xs text-muted-foreground">
              Characters used to split text into chunks
            </p>
          </div>

          {/* Auto Keywords */}
          <div className={`space-y-2 ${isParserConfigDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <Label htmlFor="auto_keywords">Auto Keywords per Chunk</Label>
            <Input
              id="auto_keywords"
              type="number"
              min={0}
              max={32}
              value={formData.auto_keywords}
              onChange={(e) => handleChange('auto_keywords', parseInt(e.target.value) || 0)}
              disabled={isParserConfigDisabled}
            />
            <p className="text-xs text-muted-foreground">
              Generate keywords automatically during parsing (0-32, 0 = disabled)
            </p>
          </div>

          {/* Auto Questions */}
          <div className={`space-y-2 ${isParserConfigDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <Label htmlFor="auto_questions">Auto Questions per Chunk</Label>
            <Input
              id="auto_questions"
              type="number"
              min={0}
              max={10}
              value={formData.auto_questions}
              onChange={(e) => handleChange('auto_questions', parseInt(e.target.value) || 0)}
              disabled={isParserConfigDisabled}
            />
            <p className="text-xs text-muted-foreground">
              Generate questions automatically during parsing (0-10, 0 = disabled)
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
